// src/features/dashboard/hooks/useTeamTable.ts

import { useEffect, useState } from 'react';

import { ENV } from '../../../config/env';

import {
  getTeamTableData,
  type TeamTablePayload,
  type DashboardGridResponse,
} from '../services/TableTeamApi';

import {
  mapTeamTableData,
  teamTableMockData,
} from '../mocks/teamTable.mock';

type TeamTableData = ReturnType<typeof mapTeamTableData>;

export const useTeamTable = (payload: TeamTablePayload) => {
  const [data, setData] = useState<TeamTableData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const managerEmployeeCode = String(payload?.managerEmployeeCode ?? '').trim();
  const fromDate = payload?.fromDate;
  const toDate = payload?.toDate;
  const teamLeadEmployeeCode = payload?.teamLeadEmployeeCode ?? null;
  const geo = payload?.geo ?? null;
  const searchText = payload?.searchText ?? null;

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!managerEmployeeCode) {
        setData(null);
        setError('');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const requestPayload: TeamTablePayload = {
          managerEmployeeCode,
          fromDate,
          toDate,
          teamLeadEmployeeCode,
          geo,
          searchText,
        };

        console.log('TEAM TABLE API REQUEST:', requestPayload);

        const response: DashboardGridResponse = ENV.USE_MOCK
          ? (teamTableMockData as DashboardGridResponse)
          : await getTeamTableData(requestPayload);

        if (cancelled) return;

        console.log('TEAM TABLE API RESPONSE:', response);

        const mapped = mapTeamTableData(response);

        if (!cancelled) {
          console.log('TEAM TABLE MAPPED DATA:', mapped);
          setData(mapped);
          setError('');
        }
      } catch (err: unknown) {
        console.error('TEAM TABLE API ERROR:', err);

        if (!cancelled) {
          setError('Failed to load team table');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadData();

    return () => {
      cancelled = true;
    };
  }, [
    managerEmployeeCode,
    fromDate,
    toDate,
    teamLeadEmployeeCode,
    geo,
    searchText,
  ]);

  return { data, loading, error };
};
